import React, { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle2, Loader2, Truck, Download, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { downloadAuthedFile } from '../utils/downloadAuthedFile';

const todayStr = () => new Date().toISOString().slice(0, 10);
const tomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export default function PickupScheduler({ order, shipmentId, existingPickup, onScheduled }) {
  const { apiCall, user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [pickupDate, setPickupDate] = useState(tomorrowStr());
  const [pickupTime, setPickupTime] = useState('14:00');
  const [sellerPhone, setSellerPhone] = useState('');
  const [profileLoaded, setProfileLoaded] = useState(false);
  // iter-298L — Reschedule flow: when true, we render the form even though
  // a pickup already exists, and pass reschedule:true to the backend so it
  // bypasses the `pickup_already_locked` guard for shipments still in the
  // PICKUP_SCHEDULED state (not yet PICKED_UP).
  const [rescheduleMode, setRescheduleMode] = useState(false);

  // Auto-fill delivery co-ordinator contact from vendor profile so the
  // rider can call the seller on arrival. Read order:
  //   1. primary pickup location's phone (set during tax registration)
  //   2. user.mobile (the canonical field on the user model — not
  //      "phone", which is empty in most accounts and was the source
  //      of the silent autofill bug)
  //   3. user.phone (legacy)
  // We NEVER block scheduling on missing phone here — backend has a
  // server-side fallback to vendor profile if the field is left blank.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let phone = '';
        try {
          const locs = await apiCall('/api/shipping/vendor/pickup-locations');
          const primary = (locs?.locations || []).find((l) => l.is_primary) || (locs?.locations || [])[0];
          phone = primary?.phone || '';
        } catch (_err) { /* ignore */ }
        if (!phone) phone = user?.mobile || user?.phone || '';
        if (!cancelled) {
          setSellerPhone(phone || '');
          setProfileLoaded(true);
        }
      } catch (_err) {
        if (!cancelled) setProfileLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall, user]);

  useEffect(() => {
    if (existingPickup) {
      setPickupDate(existingPickup.pickup_date || tomorrowStr());
      setPickupTime(existingPickup.pickup_time || '14:00');
      if (existingPickup.rider_phone) setSellerPhone(existingPickup.rider_phone);
    }
  }, [existingPickup]);

  const handleSchedule = async () => {
    if (!shipmentId) {
      toast({ title: 'No shipment', description: 'Generate AWB first', variant: 'destructive' });
      return;
    }
    const digits = sellerPhone.replace(/\D/g, '');
    if (sellerPhone && digits.length > 0 && digits.length < 10) {
      // Only block on a partial number — if the field is left empty,
      // the backend falls back to vendor.mobile / pickup-location phone
      // so the rider always has SOMEONE to call.
      toast({
        title: 'Phone looks short',
        description: 'Enter a 10-digit number or leave blank to use the number on your vendor profile.',
        variant: 'destructive',
      });
      return;
    }
    setLoading(true);
    try {
      const res = await apiCall('/api/pickup/schedule', {
        method: 'POST',
        body: JSON.stringify({
          shipment_ids: [shipmentId],
          pickup_date: pickupDate,
          pickup_time: pickupTime,
          rider_phone: sellerPhone,
          // iter-298L — Reschedule flag: bypasses pickup_already_locked when
          // the shipment is still in PICKUP_SCHEDULED (not PICKED_UP).
          reschedule: rescheduleMode || !!existingPickup?.pickup_date,
        }),
      });
      if (res.success) {
        toast({
          title: rescheduleMode ? 'Pickup rescheduled' : 'Pickup scheduled',
          description: `Delhivery will arrive on ${pickupDate} at ${pickupTime}. Reminders day-before and morning-of.`,
        });
        setRescheduleMode(false);
        onScheduled?.();
      } else {
        toast({
          title: 'Pickup failed',
          description: res.message || 'Delhivery rejected the pickup. Check your registered warehouse in Shipping Settings.',
          variant: 'destructive',
        });
      }
    } catch (e) {
      toast({ title: 'Failed', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const alreadyScheduled = existingPickup?.pickup_date;
  const showForm = !alreadyScheduled || rescheduleMode;

  return (
    <div data-testid="pickup-scheduler">
      <div className="flex items-center gap-2 mb-3">
        <Truck className="h-4 w-4 text-amber-700" />
        <h4 className="font-medium text-gray-900">
          {alreadyScheduled
            ? (rescheduleMode ? 'Reschedule Pickup' : 'Pickup Scheduled')
            : 'Schedule Pickup'}
        </h4>
        {alreadyScheduled && !rescheduleMode && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
      </div>

      {alreadyScheduled && !rescheduleMode ? (
        <div className="space-y-2 text-sm">
          <p className="flex items-center gap-1.5 text-gray-700">
            <Calendar className="h-3.5 w-3.5" />
            {new Date(existingPickup.pickup_date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            <span className="text-gray-500">at {existingPickup.pickup_time}</span>
          </p>
          {existingPickup.rider_phone && (
            <p className="flex items-center gap-1.5 text-gray-600 text-xs">
              <Phone className="h-3 w-3" />
              Seller contact: {existingPickup.rider_phone}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-2">
            Reminders will be sent the day-before (5pm) and morning-of (7am). You'll also receive a daily 7am digest email listing all pickups for the day.
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {/* iter-298L — Reschedule CTA. Toggles the form back on with the
                existing values pre-filled. Backend allows re-scheduling as
                long as the parcel hasn't yet been picked up by Delhivery. */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRescheduleMode(true)}
              data-testid="reschedule-pickup-btn"
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
            >
              <Calendar className="h-3 w-3 mr-1" />
              Reschedule Pickup
            </Button>
            {order?.label_url && (
              <Button
                size="sm"
                variant="outline"
                data-testid="pickup-download-label-btn"
                onClick={async () => {
                  const res = await downloadAuthedFile(order.label_url, `${order.id || 'order'}_label.pdf`);
                  if (!res.ok) {
                    toast({
                      title: 'Could not download label',
                      description: res.status === 401 ? 'Session expired — please refresh.' : (res.error || 'Try again from Pack & Ship.'),
                      variant: 'destructive',
                    });
                  }
                }}
              >
                <Download className="h-3 w-3 mr-1" /> Download AWB Label
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {rescheduleMode && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-xs text-amber-800" data-testid="reschedule-notice">
              You're rescheduling an existing pickup. The previous slot ({existingPickup?.pickup_date} at {existingPickup?.pickup_time}) will be replaced with the new date/time.
            </div>
          )}
          <p className="text-xs text-gray-600">Confirm pickup date + time. The rider will arrive at your registered pickup address and call the seller phone on the way.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Pickup Date</Label>
              <Input
                type="date"
                value={pickupDate}
                min={todayStr()}
                max={(() => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10); })()}
                onChange={(e) => setPickupDate(e.target.value)}
                data-testid="pickup-date-input"
              />
            </div>
            <div>
              <Label className="text-xs">Pickup Time</Label>
              <Input
                type="time"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                data-testid="pickup-time-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1.5">
              <Phone className="h-3 w-3" />
              Delivery co-ordinator contact no.
              <span className="text-[10px] text-gray-500 font-normal">(rider will call on arrival)</span>
            </Label>
            <Input
              type="tel"
              inputMode="numeric"
              maxLength={15}
              placeholder={profileLoaded ? '10-digit mobile' : 'Loading…'}
              value={sellerPhone}
              onChange={(e) => setSellerPhone(e.target.value.replace(/[^\d+]/g, ''))}
              data-testid="seller-phone-input"
              className="font-mono"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Auto-filled from your profile. Edit if you'd like the rider to call a different number for this pickup.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSchedule} disabled={loading} size="sm" data-testid="confirm-pickup-btn">
              {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <Clock className="h-3 w-3 mr-1.5" />}
              {rescheduleMode ? 'Confirm Reschedule' : 'Confirm Pickup'}
            </Button>
            {rescheduleMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRescheduleMode(false)}
                data-testid="cancel-reschedule-btn"
              >
                Keep Existing
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
